import { SubmissionReviewComponent } from '@program-suite/ui-submission-review';
import { Component, inject, OnDestroy, computed, Injector } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';

import {
  MissingInfoModalComponent,
  MissingInfoModalData,
  ModalLayoutComponent,
  ModalService,
  ModalType,
  SnackbarComponent,
  ToastType,
} from '@program-suite/ui';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PendingRequestGridRowViewModel } from '@program-suite/data-common/program';
import { PendingRequestStore } from '../data/pending-request.store';
import { PendingRequestService } from '../data/pending-request.service';
import { ActionFormComponent } from './action-form/action-form.component';
import { LookupStore, ViewSubmissionStore, WizardClassViewModel, WizardStore } from '@program-suite/data-common';
import { IconComponent } from '@program-suite/util-icons';
import { toDateOnly } from '@program-suite/util-helpers';
import { LossRunsFormComponent } from "./loss-runs-form/loss-runs-form.component";
import { UiLossRunsReviewComponent } from "./ui-loss-runs-review/ui-loss-runs-review.component";

@Component({
  selector: 'lib-pending-request-modal',
  templateUrl: './pending-request-modal.component.html',
  styleUrl: './pending-request-modal.component.scss',
  imports: [
    ModalLayoutComponent,
    ActionFormComponent,
    SubmissionReviewComponent,
    LossRunsFormComponent,
    UiLossRunsReviewComponent,
    IconComponent
],
  // The renewal payroll modal's owner payroll and percentage totals read this
  providers: [WizardStore],
})
export class PendingRequestModalComponent implements OnDestroy {
  data: PendingRequestGridRowViewModel = inject(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<PendingRequestModalComponent>);
  pendingRequestStore = inject(PendingRequestStore);
  viewSubmissionStore = inject(ViewSubmissionStore);
  private pendingRequestService = inject(PendingRequestService);
  private wizardStore = inject(WizardStore);
  private lookupStore = inject(LookupStore);
  private modalService = inject(ModalService);
  private snackBar = inject(MatSnackBar);
  private injector = inject(Injector);
  private fb = inject(FormBuilder);
  title = computed<string>(() => {
    const { requestType = '', submissionType = '' } = this.pendingRequestStore.submissionReview?.() ?? {};
    const isRenewal = submissionType?.toLowerCase() === 'renewal';
    const hasRenewal = requestType?.toLowerCase().includes('renewal');

    return requestType
      ? `${isRenewal && !hasRenewal ? 'Renewal ' : ''}${requestType} Request`
      : '';
  });
  proposedEffectiveDate: string | null | undefined = null;
  constructor() {
    if(this.data.requestType !== 'Loss Runs'){
    this.proposedEffectiveDate = this.data.proposedEffectiveDate !== this.data.effectiveDate ? this.data.proposedEffectiveDate : null;
    this.pendingRequestStore.getSubmissionReview(this.data.id ?? '');
    this.viewSubmissionStore.getSubmissionDocuments(this.data.id ?? '', false);
    }
    else{
      this.pendingRequestStore.getLossRunReview(this.data.id!)
    }
  }

  // Renewal automation defaulted owners, employee payroll, and percentage of work; saving re-rates with what's entered here
  async editRenewalPayroll() {
    const wizard = await this.pendingRequestService.getWizard(this.data.id!);
    this.lookupStore.loadProgramClasses('1', wizard.state!, toDateOnly(wizard.effectiveDate!));
    const form: FormGroup = this.fb.group({
      numberOfFieldOwners: [wizard.numberOfFieldOwners, Validators.required],
      // Renewal automation leaves the count blank for older policies
      numberOfEmployees: [wizard.numberOfEmployees ?? 0, [Validators.required, this.numberOfEmployeesValidator()]],
      employeePayroll: [wizard.employeePayroll, [Validators.required, this.employeePayrollValidator()]],
      classes: this.fb.array(
        (wizard.classes ?? []).map((c) =>
          this.fb.group({
            classCode: [c.classCode, Validators.required],
            payroll: [c.payroll],
            percentageOfWork: [c.percentageOfWork, [Validators.required, Validators.min(1), Validators.max(100)]],
          })
        )
      ),
    });
    // The whole wizard, not just the form: the class code eligibility rule also reads performsNewResConstruction
    this.wizardStore.setClassesInitialized(true);
    this.wizardStore.updateFields(wizard);
    const formChanges = form.valueChanges.subscribe(() => this.wizardStore.updateFields(form.getRawValue()));
    const employeeChanges = form.get('numberOfEmployees')!.valueChanges.subscribe(() => {
      const employeePayroll = form.get('employeePayroll')!;
      employeePayroll.updateValueAndValidity();
      if (employeePayroll.invalid) {
        employeePayroll.markAsTouched();
      }
    });

    const data: MissingInfoModalData = {
      form,
      // Same rule as coverage adjustment, less total payroll: class payroll isn't recalculated until the server saves
      disabled: computed(
        () =>
          this.wizardStore.addAppropriateClassCodeError() ||
          this.wizardStore.totalClassPercentage() !== 100 ||
          this.wizardStore.duplicateClasses() ||
          this.wizardStore.employeePayrollMissing()
      ),
      title: 'Renewal Payroll',
      message: 'Renewal automation defaulted the owners, employee payroll, and work percentages. Update them and the renewal will be re-rated.',
      buttonText: 'Save and Re-rate',
      returnText: 'Cancel',
    };
    this.modalService.open(
      MissingInfoModalComponent,
      { modalType: ModalType.Large, disableClose: true, data, injector: this.injector, stacked: true },
      async (save: boolean) => {
        formChanges.unsubscribe();
        employeeChanges.unsubscribe();
        if (!save) {
          return;
        }
        const value = form.getRawValue();
        try {
          await this.pendingRequestService.updateRenewalPayroll(this.data.id!, {
            numberOfFieldOwners: Number(value.numberOfFieldOwners),
            numberOfEmployees: Number(value.numberOfEmployees),
            employeePayroll: Number(String(value.employeePayroll ?? '0').replace(/[^0-9.-]+/g, '')),
            classes: value.classes.map((c: WizardClassViewModel) => ({ classCode: c.classCode, percentageOfWork: Number(c.percentageOfWork) })),
          });
        } catch (error: any) {
          this.snackBar.openFromComponent(SnackbarComponent, {
            duration: 5000,
            data: { title: 'Rating Failed', toastType: ToastType.Error, message: error?.error ?? error },
          });
          return;
        }
        await this.pendingRequestStore.getSubmissionReview(this.data.id!);
      }
    );
  }

  // Same rules as the quote wizard and coverage adjustment. Uses the 'message' error so lib-form-dropdown shows it,
  // e.g. for older quotes saved with more than 20 employees when this was a free-text field.
  private numberOfEmployeesValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value == null || control.value === '') {
        return null;
      }
      const employees = Number(control.value);
      return Number.isInteger(employees) && employees >= 0 && employees <= 20
        ? null
        : { message: 'Number of Employees must be between 0 and 20.' };
    };
  }

  // Employee payroll can only be $0 when there are no employees. Uses the 'message' error so
  // lib-form-money shows it in the field's own error space instead of pushing the row down.
  private employeePayrollValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const employees = Number(control.parent?.get('numberOfEmployees')?.value) || 0;
      const payroll = Number(String(control.value ?? '0').replace(/[^0-9.-]+/g, '')) || 0;
      return employees > 0 && payroll <= 0
        ? { message: 'Employee Payroll must be greater than $0 when the applicant has employees.' }
        : null;
    };
  }

  ngOnDestroy(): void {
    this.pendingRequestStore.setSubmissionReview(null);
    this.pendingRequestStore.setLossRunReview(null);
  }
}
